import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import {
  fetchMerchantEvents, eventImage, eventShortDate, eventTimeLabel, isEventPast,
  type PublicEvent,
} from '@/lib/publicEvents';

// ─────────────────────────────────────────────────────────────
// Section « Événements » de la page d'accueil publique.
// Affichée uniquement si le marchand a publié au moins un
// événement à venir. Grille 3×3×3 (2 sur mobile).
// ─────────────────────────────────────────────────────────────

export default function HomeEventsSection() {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;
  const [events, setEvents] = useState<PublicEvent[]>([]);

  useEffect(() => {
    if (!isTenant || !merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchMerchantEvents(Number(merchantId));
        if (!cancelled) setEvents(rows.filter((ev) => !isEventPast(ev)).slice(0, 3));
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId]);

  if (events.length === 0) return null;

  return (
    <section className="py-10 md:py-16">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-3 mb-5 md:mb-7">
          <div className="min-w-0">
            <span className="block h-1 w-10 rounded-full bg-accent-500 mb-2.5"></span>
            <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
              <i className="ri-calendar-event-line text-primary-500 text-base md:text-xl"></i>
              <span className="truncate">Prochains événements</span>
            </h2>
          </div>
          <Link
            to="/events"
            className="flex items-center gap-1 text-xs md:text-sm text-primary-600 hover:text-primary-700 font-semibold no-underline whitespace-nowrap flex-shrink-0"
          >
            Tous les événements <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {events.map((ev) => {
            const cover = eventImage(ev);
            const date = eventShortDate(ev.start_datetime);
            const time = eventTimeLabel(ev.start_datetime);
            return (
              <Link
                key={ev.id}
                to={`/events/${ev.id}`}
                className="group relative flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/80 no-underline"
              >
                <div className="relative aspect-[16/10] bg-background-100 overflow-hidden">
                  {cover ? (
                    <img
                      src={cover}
                      alt={ev.title || 'Événement'}
                      title={`${ev.title || 'Événement'} — agenda`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 to-accent-50">
                      <i className="ri-calendar-event-line text-4xl text-secondary-300"></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/5 to-transparent"></div>
                  <div className="absolute top-2.5 left-2.5 rounded-lg bg-background-50/95 px-2 py-1 text-center min-w-[44px]">
                    <p className="text-sm font-bold font-heading text-foreground-950 leading-none">{date.day}</p>
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-accent-700 mt-0.5">{date.month}</p>
                  </div>
                </div>
                <div className="flex flex-col flex-1 p-3.5 md:p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                    {ev.title || 'Événement'}
                  </h3>
                  <p className="mt-1.5 text-[11px] md:text-xs text-foreground-500 truncate">
                    <i className="ri-calendar-line mr-1"></i>
                    {date.full}{time ? ` · ${time}` : ''}
                  </p>
                  {ev.description && (
                    <p className="mt-2 text-[11px] md:text-xs text-foreground-600 leading-relaxed line-clamp-2">
                      {ev.description}
                    </p>
                  )}
                  <span className="mt-3 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] md:text-xs font-semibold bg-primary-50 text-primary-700 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors whitespace-nowrap">
                    <i className="ri-ticket-2-line"></i>
                    S&apos;inscrire
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}